import axiosInstance from '../axiosConfig';
import { NonMember, NonMemberRegisterData, GuestPass } from '../../types';

/**
 * Non-Member Service
 * Handles all non-member related API calls
 */
class NonMemberService {
  /**
   * Register a new non-member (public endpoint)
   * @param data - Registration data
   * @returns Non-member data with access token
   */
  async register(data: NonMemberRegisterData): Promise<{ success: boolean; data: NonMember; access_token: string }> {
    const response = await axiosInstance.post('/non-members/register', data);
    return response.data;
  }

  /**
   * Get all non-members (admin/staff only)
   * @param includeInactive - Include inactive non-members
   * @param filters - Optional filters
   * @returns List of non-members
   */
  async getAllNonMembers(
    includeInactive: boolean = false,
    filters?: {
      status?: string;
      acquisition_source?: string;
      conversion_interest?: string;
    }
  ): Promise<{ success: boolean; data: NonMember[]; count: number }> {
    const response = await axiosInstance.get('/non-members', {
      params: {
        include_inactive: includeInactive.toString(),
        ...filters,
      },
    });
    return response.data;
  }

  /**
   * Search non-members by name, email, or phone
   * @param query - Search query
   * @param includeInactive - Include inactive non-members
   * @returns List of matching non-members
   */
  async searchNonMembers(
    query: string,
    includeInactive: boolean = false
  ): Promise<{ success: boolean; data: NonMember[]; count: number }> {
    const response = await axiosInstance.get('/non-members/search', {
      params: {
        q: query,
        include_inactive: includeInactive.toString(),
      },
    });
    return response.data;
  }

  /**
   * Search non-members for booking purposes (available to all authenticated users)
   * @param query - Search query (name, email, or member number)
   * @param limit - Maximum results (default 10)
   * @returns List of matching non-members with limited fields
   */
  async searchNonMembersForBooking(
    query: string,
    limit: number = 10
  ): Promise<{ success: boolean; data: NonMember[]; count: number }> {
    const response = await axiosInstance.get('/non-members/search-for-booking', {
      params: { q: query, limit },
    });
    return response.data;
  }

  /**
   * Get a non-member by ID (admin/staff only)
   * @param nonMemberId - Non-member ID
   * @returns Non-member data
   */
  async getNonMemberById(nonMemberId: string): Promise<{ success: boolean; data: NonMember }> {
    const response = await axiosInstance.get(`/non-members/${nonMemberId}`);
    return response.data;
  }

  /**
   * Update a non-member's profile (admin/staff only)
   * @param nonMemberId - Non-member ID
   * @param data - Update data
   * @returns Updated non-member data
   */
  async updateNonMember(
    nonMemberId: string,
    data: Partial<NonMember>
  ): Promise<{ success: boolean; data: NonMember }> {
    const response = await axiosInstance.put(`/non-members/${nonMemberId}`, data);
    return response.data;
  }

  /**
   * Get guest visit status for a non-member
   * @param nonMemberId - Non-member ID
   * @returns Guest visit status
   */
  async getGuestVisitStatus(nonMemberId: string): Promise<{
    success: boolean;
    data: {
      visits_this_year: number;
      annual_limit: number;
      remaining_visits: number;
      reset_date: string | null;
    };
  }> {
    const response = await axiosInstance.get(`/non-members/${nonMemberId}/guest-visits`);
    return response.data;
  }

  /**
   * Update conversion interest level
   * @param nonMemberId - Non-member ID
   * @param interestLevel - Interest level (none, low, medium, high, ready_to_apply)
   * @param notes - Optional notes
   * @returns Updated non-member data
   */
  async updateConversionInterest(
    nonMemberId: string,
    interestLevel: string,
    notes?: string
  ): Promise<{ success: boolean; data: NonMember }> {
    const response = await axiosInstance.put(`/non-members/${nonMemberId}/conversion-interest`, {
      interest_level: interestLevel,
      notes,
    });
    return response.data;
  }

  /**
   * Block a non-member (admin only)
   * @param nonMemberId - Non-member ID
   * @param reason - Reason for blocking
   * @returns Updated non-member data
   */
  async blockNonMember(
    nonMemberId: string,
    reason: string
  ): Promise<{ success: boolean; data: NonMember }> {
    const response = await axiosInstance.post(`/non-members/${nonMemberId}/block`, { reason });
    return response.data;
  }

  /**
   * Unblock a non-member (admin only)
   * @param nonMemberId - Non-member ID
   * @returns Updated non-member data
   */
  async unblockNonMember(nonMemberId: string): Promise<{ success: boolean; data: NonMember }> {
    const response = await axiosInstance.post(`/non-members/${nonMemberId}/unblock`);
    return response.data;
  }

  /**
   * Issue a guest pass to a non-member
   * @param data - Guest pass data
   * @returns Created guest pass
   */
  async issueGuestPass(data: {
    guest_member_id: string;
    sponsor_member_id: string;
    visit_date: string;
    facilities_allowed?: string[];
    guest_fee_amount?: number;
    fee_billed_to?: string;
    notes?: string;
  }): Promise<{ success: boolean; data: GuestPass }> {
    const response = await axiosInstance.post('/guest-passes', data);
    return response.data;
  }

  /**
   * Get guest passes for a non-member
   * @param nonMemberId - Non-member ID
   * @returns List of guest passes
   */
  async getGuestPasses(nonMemberId: string): Promise<{ success: boolean; data: GuestPass[]; count: number }> {
    const response = await axiosInstance.get(`/guest-passes/by-guest/${nonMemberId}`);
    return response.data;
  }
}

// Export singleton instance
export const nonMemberService = new NonMemberService();
export default nonMemberService;
